const DEFAULT_LANGUAGE = 'fr';
const SUPPORTED_LANGUAGES = ['fr', 'en', 'ar'];
const STORAGE_KEY = 'site_language';
const GOOGLE_UI_SELECTORS = [
  '.goog-te-banner-frame',
  '.goog-te-banner-frame.skiptranslate',
  '.goog-te-balloon-frame',
  '.goog-te-menu-frame',
  '.goog-te-ftab-frame',
  '#goog-gt-tt',
  '.goog-tooltip',
  '.goog-te-spinner-pos',
  '.VIpgJd-ZVi9od-ORHb',
  '.VIpgJd-ZVi9od-ORHb-OEVmcd',
  '.VIpgJd-ZVi9od-aZ2wEe-wOHMyf',
  '.VIpgJd-ZVi9od-aZ2wEe-OiiCO',
  '.VIpgJd-ZVi9od-l4eHX-hSRGPd',
  '.VIpgJd-ZVi9od-xl07Ob-OEVmcd'
];

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const isSupportedLanguage = lang => SUPPORTED_LANGUAGES.includes(lang);
let googleUICleanupObserver = null;

const hideGoogleUIElement = element => {
  if (!element) return;
  element.style.setProperty('display', 'none', 'important');
  element.style.setProperty('visibility', 'hidden', 'important');
  element.style.setProperty('opacity', '0', 'important');
  element.style.setProperty('height', '0', 'important');
  element.style.setProperty('min-height', '0', 'important');
};

const hideGoogleTranslateUI = () => {
  if (!isBrowser()) return;

  GOOGLE_UI_SELECTORS.forEach(selector => {
    document.querySelectorAll(selector).forEach(hideGoogleUIElement);
  });

  document.querySelectorAll('iframe.goog-te-banner-frame, iframe.skiptranslate').forEach(hideGoogleUIElement);

  if (document.body) {
    document.body.style.setProperty('top', '0px', 'important');
  }
  document.documentElement.style.setProperty('top', '0px', 'important');
};

const scheduleGoogleUICleanup = () => {
  if (!isBrowser()) return;
  hideGoogleTranslateUI();
  [100, 300, 700, 1200, 2000].forEach(delay => {
    window.setTimeout(hideGoogleTranslateUI, delay);
  });
};

const ensureGoogleUICleanupObserver = () => {
  if (!isBrowser() || googleUICleanupObserver) return;

  googleUICleanupObserver = new MutationObserver(() => {
    hideGoogleTranslateUI();
  });

  googleUICleanupObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
};

const getLanguageFromCookie = () => {
  if (!isBrowser()) return null;
  const match = document.cookie.match(/(?:^|;\s*)googtrans=\/fr\/([a-z-]+)/i);
  return match ? match[1] : null;
};

export const getSavedLanguage = () => {
  if (!isBrowser()) return DEFAULT_LANGUAGE;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (isSupportedLanguage(stored)) return stored;

  const fromCookie = getLanguageFromCookie();
  if (isSupportedLanguage(fromCookie)) return fromCookie;

  return DEFAULT_LANGUAGE;
};

export const applyLanguageMetadata = lang => {
  if (!isBrowser()) return;
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  const isTranslated = lang !== DEFAULT_LANGUAGE;
  document.documentElement.classList.toggle('is-translated', isTranslated);
  if (document.body) {
    document.body.classList.toggle('is-translated', isTranslated);
  }
};

const setTranslateCookie = lang => {
  if (!isBrowser()) return;
  const value = `/fr/${lang}`;
  document.cookie = `googtrans=${value};path=/`;
};

export const applyGoogleTranslation = lang => {
  if (!isBrowser()) return false;

  const combo = document.querySelector('.goog-te-combo');
  if (!combo) return false;

  combo.value = lang;
  combo.dispatchEvent(new Event('change'));
  scheduleGoogleUICleanup();
  return true;
};

export const setSiteLanguage = lang => {
  if (!isBrowser() || !isSupportedLanguage(lang)) return;

  localStorage.setItem(STORAGE_KEY, lang);
  setTranslateCookie(lang);
  applyLanguageMetadata(lang);
  scheduleGoogleUICleanup();

  // If the Google combo is not ready yet, a refresh ensures translation is applied.
  const applied = applyGoogleTranslation(lang);
  if (!applied) window.location.reload();
};

export const initGoogleTranslate = () => {
  if (!isBrowser()) return;

  const savedLanguage = getSavedLanguage();
  applyLanguageMetadata(savedLanguage);
  setTranslateCookie(savedLanguage);
  ensureGoogleUICleanupObserver();
  scheduleGoogleUICleanup();

  if (!window.googleTranslateElementInit) {
    window.googleTranslateElementInit = () => {
      if (!window.google || !window.google.translate || !window.google.translate.TranslateElement) return;
      // eslint-disable-next-line no-new
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'fr',
          includedLanguages: SUPPORTED_LANGUAGES.join(','),
          autoDisplay: false
        },
        'google_translate_element'
      );
      scheduleGoogleUICleanup();
    };
  }

  if (!document.querySelector('script[data-google-translate]')) {
    const script = document.createElement('script');
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    script.setAttribute('data-google-translate', 'true');
    document.body.appendChild(script);
  }
};
